import { EnvironmentOutlined } from "@ant-design/icons"
import { Checkbox, Col, Flex, Input, Row, Select, Typography } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { AddressAutoComplete } from "../../../../components/AutoComplete/AddressAutoComplete.tsx"
import { ZipLookupHint } from "../../../../components/AutoComplete/ZipLookupHint.tsx"
import { AddressMapPreview } from "../../../../components/Shared/AddressMapPreview.tsx"
import { AddressQuickLinks } from "../../../../components/Shared/AddressQuickLinks.tsx"
import { useZipLookup } from "../../../../hooks/use-zip-lookup.ts"
import { zipCodeRegexUS } from "../../../../utils/validation/zip-code.ts"
import { type LeadsFilterOptions } from "../../../Leads/redux/leads.types.ts"
import { type LeadFormDataCustomer } from "../../LeadAddPage/components/lead-form-data.ts"
import { applyAddressSuggestion } from "../../helpers/address-suggestion.ts"
import { type Lead } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import {
  type LeadPatchSender,
  type LeadWorkspacePatch,
  pickChangedFields
} from "../lead-workspace-patch.ts"
import { LeadOverviewField } from "./LeadOverviewField.tsx"
import {
  leadCustomerAddressLine,
  leadCustomerValues
} from "./lead-overview-customer.ts"
import { OverviewCardTitle } from "./OverviewCardIcon.tsx"

interface AddressValues {
  address?: string | null
  address_2?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
}

interface LocationCardValues extends AddressValues {
  customer: LeadFormDataCustomer
  use_customer_address: boolean
}

const propertyAddressLine = (lead: AddressValues | null) =>
  [lead?.address, lead?.address_2, lead?.city, lead?.state, lead?.zip_code]
    .filter(Boolean)
    .join(", ") || null

/** The same rule the form's map preview applies: city and state, valid ZIP. */
function mapAddress(values: {
  address?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
}): string | null {
  const city = values.city?.trim()
  const state = values.state?.trim()
  if (!city || !state) return null

  const zip = values.zip_code?.trim().toUpperCase()
  const parts = [
    values.address?.trim(),
    city,
    state,
    zip && zipCodeRegexUS.test(zip) ? zip : null
  ].filter(Boolean)

  return `${parts.join(", ")}, USA`
}

interface LocationAddressEditorProps {
  title: string
  values: AddressValues
  disabled: boolean
  knownStates: string[]
  stateOptions: LeadsFilterOptions["state"]
  onChange: (patch: Partial<AddressValues>) => void
  ariaPrefix?: string
  autoFocus?: boolean
}

function LocationAddressEditor({
  title,
  values,
  disabled,
  knownStates,
  stateOptions,
  onChange,
  ariaPrefix,
  autoFocus
}: LocationAddressEditorProps) {
  const { t } = useTranslation()
  const ariaLabel = (label: string) =>
    ariaPrefix ? `${ariaPrefix} ${label}` : label

  const { lookupZip, chooseMatch, suppressLookupFor, status } = useZipLookup({
    knownStates,
    disabled,
    initialZip: values.zip_code,
    onFill: onChange
  })

  return (
    <>
      <Col xs={24}>
        <Typography.Text strong>{title}</Typography.Text>
      </Col>
      <Col xs={24}>
        <LeadOverviewField
          label={t("leadWorkspace.cards.location.addressSearch")}
        >
          <AddressAutoComplete
            disabled={disabled}
            placeholder={
              ariaPrefix ? `Search ${title.toLowerCase()}...` : undefined
            }
            onSelect={(suggestion) => {
              const applied = applyAddressSuggestion(suggestion, knownStates)
              suppressLookupFor(applied.zip_code)
              onChange(applied)
            }}
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={12}>
        <LeadOverviewField label={t("leadWorkspace.cards.location.address")}>
          <Input
            autoFocus={autoFocus}
            disabled={disabled}
            aria-label={ariaLabel(
              t("leadWorkspace.cards.location.address")
            )}
            value={values.address ?? ""}
            onChange={(event) =>
              onChange({ address: event.target.value || null })
            }
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={12}>
        <LeadOverviewField label={t("leadWorkspace.cards.location.address2")}>
          <Input
            disabled={disabled}
            aria-label={ariaLabel(
              t("leadWorkspace.cards.location.address2")
            )}
            value={values.address_2 ?? ""}
            onChange={(event) =>
              onChange({ address_2: event.target.value || null })
            }
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={8}>
        <LeadOverviewField label={t("leadWorkspace.cards.location.city")}>
          <Input
            disabled={disabled}
            aria-label={ariaLabel(t("leadWorkspace.cards.location.city"))}
            value={values.city ?? ""}
            onChange={(event) =>
              onChange({ city: event.target.value || null })
            }
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={8}>
        <LeadOverviewField label={t("leadWorkspace.cards.location.state")}>
          <Select
            allowClear
            showSearch
            className={"w-full"}
            disabled={disabled}
            aria-label={ariaLabel(t("leadWorkspace.cards.location.state"))}
            optionFilterProp={"label"}
            options={stateOptions ?? []}
            value={values.state ?? undefined}
            onChange={(next) => onChange({ state: next ?? null })}
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={8}>
        <LeadOverviewField label={t("leadWorkspace.cards.location.zip")}>
          <Input
            disabled={disabled}
            aria-label={ariaLabel(t("leadWorkspace.cards.location.zip"))}
            value={values.zip_code ?? ""}
            onChange={(event) => {
              onChange({ zip_code: event.target.value || null })
              lookupZip(event.target.value)
            }}
          />
          <ZipLookupHint status={status} onChoose={chooseMatch} />
        </LeadOverviewField>
      </Col>
    </>
  )
}

interface LocationEditBodyProps {
  values: LocationCardValues
  setValues: (patch: Partial<LocationCardValues>) => void
  saving: boolean
  linkedCustomerId: string | null
  knownStates: string[]
  stateOptions: LeadsFilterOptions["state"]
  applyCustomerAddress: (
    address: LeadFormDataCustomer
  ) => Partial<LocationCardValues>
}

/**
 * Rendered only while the card is in edit mode, so its `sameAsProperty` state
 * resets every time editing reopens rather than lingering after a cancel.
 */
function LocationEditBody({
  values,
  setValues,
  saving,
  linkedCustomerId,
  knownStates,
  stateOptions,
  applyCustomerAddress
}: LocationEditBodyProps) {
  const { t } = useTranslation()
  const [sameAsProperty, setSameAsProperty] = useState(false)

  const applyPropertyAddress = (
    current: LocationCardValues
  ): Partial<LocationCardValues> => ({
    customer: {
      ...current.customer,
      address: current.address ?? null,
      address_2: current.address_2 ?? null,
      city: current.city ?? null,
      state: current.state ?? null,
      zip_code: current.zip_code ?? null
    },
    use_customer_address: true
  })

  // The property follows the customer only in the forward direction.
  const propertyLinked = values.use_customer_address && !sameAsProperty

  return (
    <Row gutter={[12, 8]}>
      {!!linkedCustomerId &&
        !!values.address &&
        (!values.use_customer_address || sameAsProperty) && (
          <Col xs={24}>
            <Checkbox
              disabled={saving}
              checked={sameAsProperty}
              onChange={(event) => {
                if (event.target.checked) {
                  setSameAsProperty(true)
                  setValues(applyPropertyAddress(values))
                } else {
                  setSameAsProperty(false)
                  setValues({ use_customer_address: false })
                }
              }}
            >
              {t("leadWorkspace.cards.location.applyPropertyAddress")}
            </Checkbox>
          </Col>
        )}
      <LocationAddressEditor
        title={t("leadWorkspace.cards.location.customerAddress")}
        values={values.customer}
        disabled={saving || sameAsProperty}
        knownStates={knownStates}
        stateOptions={stateOptions}
        ariaPrefix={t("leadWorkspace.cards.customer.title")}
        onChange={(customerPatch) => {
          const nextCustomer = {
            ...values.customer,
            ...customerPatch
          }
          setValues({
            customer: nextCustomer,
            ...(propertyLinked ? applyCustomerAddress(nextCustomer) : {})
          })
        }}
      />
      {!!linkedCustomerId && !!values.customer.address && !sameAsProperty && (
        <Col xs={24}>
          <Checkbox
            disabled={saving}
            checked={values.use_customer_address}
            onChange={(event) =>
              setValues(
                event.target.checked
                  ? applyCustomerAddress(values.customer)
                  : { use_customer_address: false }
              )
            }
          >
            {t("leadWorkspace.cards.location.sameAsCustomer")}
          </Checkbox>
        </Col>
      )}
      <LocationAddressEditor
        // Remount so the hook re-reads the ZIP the copied address left in the fields.
        key={String(propertyLinked)}
        title={t("leadWorkspace.cards.location.propertyAddress")}
        values={values}
        disabled={saving || propertyLinked}
        knownStates={knownStates}
        stateOptions={stateOptions}
        autoFocus
        onChange={(propertyPatch) => {
          const nextProperty = { ...values, ...propertyPatch }
          setValues({
            ...propertyPatch,
            ...(sameAsProperty ? applyPropertyAddress(nextProperty) : {})
          })
        }}
      />
    </Row>
  )
}

interface LeadOverviewLocationCardProps {
  lead: Lead
  filterOptions: LeadsFilterOptions
  canEdit?: boolean
  onPatch: LeadPatchSender
}

function LeadOverviewLocationCard({
  lead,
  filterOptions,
  canEdit,
  onPatch
}: LeadOverviewLocationCardProps) {
  const { t } = useTranslation()

  const knownStates = useMemo(
    () => (filterOptions?.state ?? []).map((option) => String(option?.value)),
    [filterOptions?.state]
  )

  const customer = leadCustomerValues(lead)
  const customerAddress = leadCustomerAddressLine(customer)
  const linkedCustomerId = lead?.customer?.id ?? null

  const values: LocationCardValues = {
    customer,
    address: lead?.address ?? null,
    address_2: lead?.address_2 ?? null,
    city: lead?.city ?? null,
    state: lead?.state ?? null,
    zip_code: lead?.zip_code ?? null,
    use_customer_address: !!lead?.use_customer_address
  }

  const applyCustomerAddress = (
    address: LeadFormDataCustomer
  ): Partial<LocationCardValues> => ({
    address: address.address ?? null,
    address_2: address.address_2 ?? null,
    city: address.city ?? null,
    state: address.state ?? null,
    zip_code: address.zip_code ?? null,
    use_customer_address: true
  })

  const saveLocation = (changed: Partial<LocationCardValues>) => {
    const patch: LeadWorkspacePatch = { ...changed }

    if (changed.customer) {
      const editedCustomer = pickChangedFields(customer, changed.customer)
      if (Object.keys(editedCustomer).length) {
        patch.customer = editedCustomer
        if (linkedCustomerId) patch.customer_id = linkedCustomerId
      } else delete patch.customer
    }

    return onPatch(patch)
  }

  return (
    <WorkspaceCard<LocationCardValues>
      cardId={"location"}
      title={
        <OverviewCardTitle icon={<EnvironmentOutlined />} accent={"indigo"}>
          {t("leadWorkspace.cards.location.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("leadWorkspace.cards.location.title")}
      className={"h-full"}
      canEdit={canEdit}
      values={values}
      onSave={saveLocation}
      renderEdit={({ values, setValues, saving }) => (
        <LocationEditBody
          values={values}
          setValues={setValues}
          saving={saving}
          linkedCustomerId={linkedCustomerId}
          knownStates={knownStates}
          stateOptions={filterOptions?.state}
          applyCustomerAddress={applyCustomerAddress}
        />
      )}
    >
      <Row gutter={[16, 12]} align={"stretch"} className={"flex-1"}>
        <Col xs={24} md={16}>
          <Flex vertical justify={"center"} gap={"middle"} className={"h-full"}>
            <Row gutter={[16, 8]}>
              <Col
                span={12}
                className={"border-r border-border"}
              >
                <LeadOverviewField
                  label={t("leadWorkspace.cards.location.customerAddress")}
                  value={customerAddress}
                />
              </Col>
              <Col span={12}>
                <LeadOverviewField
                  label={t("leadWorkspace.cards.location.propertyAddress")}
                  value={propertyAddressLine(lead)}
                />
                <AddressQuickLinks
                  className="mt-1"
                  address={lead.address}
                  address_2={lead.address_2}
                  city={lead.city}
                  state={lead.state}
                  zip_code={lead.zip_code}
                  geolocation={lead.geolocation}
                />
              </Col>
            </Row>
            {!!lead?.use_customer_address && (
              <Checkbox checked disabled>
                {t("leadWorkspace.cards.location.sameAsCustomer")}
              </Checkbox>
            )}
          </Flex>
        </Col>
        <Col xs={24} md={8} className={"min-h-[160px]"}>
          <AddressMapPreview
            height={"100%"}
            className={"mb-0 h-full"}
            fullAddress={mapAddress(lead)}
            emptyText={t("leadWorkspace.cards.location.noMap")}
          />
        </Col>
      </Row>
    </WorkspaceCard>
  )
}

export {
  type LocationCardValues,
  mapAddress,
  propertyAddressLine,
  LeadOverviewLocationCard
}
